const DARK = {
    bg:          '#1e2a32',
    bgHover:     '#2e404f',
    border:      '#3a4d5c',
    focusBorder: '#3c8dbc',
    text:        '#d0d8dc',
    textMuted:   'rgba(208,216,220,0.5)',
};

export function makeDarkSelectStyles({ width = '200px', border, marginBottom } = {}) {
    const full = width === '100%';
    return {
        container: (base) => ({
            ...base,
            width: full ? '100%' : width,
            display: full ? 'block' : base.display,
        }),
        control: (base, state) => ({
            ...base,
            background: DARK.bg,
            backgroundColor: DARK.bg,
            width: full ? '100%' : width,
            minWidth: full ? 0 : undefined,
            border: border ? `1px solid ${DARK.border}` : 'none',
            borderColor: DARK.border,
            boxShadow: state.isFocused ? `0 0 0 1px ${DARK.focusBorder}` : null,
            ...(marginBottom ? { marginBottom } : {}),
            '&:hover': {
                borderColor: DARK.focusBorder,
            },
        }),
        menu: (base) => ({
            ...base,
            width,
            background: DARK.bg,
            backgroundColor: DARK.bg,
            borderColor: DARK.border,
            borderRadius: 0,
            borderBottomRightRadius: 5,
            borderBottomLeftRadius: 5,
            marginTop: 0,
            opacity: 1,
            '*::-webkit-scrollbar': { width: '0.4em' },
            '*::-webkit-scrollbar-track': { '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.3)' },
            '*::-webkit-scrollbar-thumb': { backgroundColor: DARK.border },
        }),
        menuList: (base) => ({
            ...base,
            padding: 10,
            background: DARK.bg,
            backgroundColor: DARK.bg,
        }),
        option: (base, state) => ({
            ...base,
            color: DARK.text,
            backgroundColor: state.isSelected || state.isFocused || state.isActive
                ? DARK.bgHover
                : DARK.bg,
            transition: 'all 0.2s ease-out',
        }),
        singleValue: (base) => ({
            ...base,
            color: DARK.text,
            backgroundColor: 'transparent',
        }),
        placeholder: (base) => ({
            ...base,
            color: DARK.textMuted,
        }),
        input: (base) => ({
            ...base,
            color: DARK.text,
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: DARK.border,
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: DARK.textMuted,
            '&:hover': { color: DARK.text },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: DARK.textMuted,
            '&:hover': { color: DARK.text },
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: DARK.bgHover,
            borderRadius: 4,
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: DARK.text,
            padding: '2px 6px',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: DARK.textMuted,
            borderRadius: '0 4px 4px 0',
            ':hover': {
                backgroundColor: DARK.border,
                color: DARK.text,
            },
        }),
    };
}

/** Stack portaled menus above CMS layout (e.g. sibling .box). */
export const CMS_SELECT_PORTAL_Z = 10050;

export function withMenuPortal(styles) {
    return {
        ...styles,
        menuPortal: (base) => ({ ...base, zIndex: CMS_SELECT_PORTAL_Z }),
    };
}

const USERS_FILTER = {
    ctrlBg: '#556576',
    ctrlBorder: '#9eb2c4',
    menuBg: '#5d6e82',
    menuHover: '#7d91a8',
    menuBorder: '#b0c0d0',
    text: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.75)',
};

/**
 * High-contrast selects for Users filter: menu rendered via portal so it is not
 * clipped or covered by siblings; white text on mid slate.
 */
export function getUsersFilterSelectStyles({ width = '200px', border, marginBottom } = {}) {
    const base = makeDarkSelectStyles({ width, border, marginBottom });
    return withMenuPortal({
        ...base,
        control: (b, state) => {
            const merged = base.control(b, state);
            return {
                ...merged,
                background: USERS_FILTER.ctrlBg,
                border: border ? `1px solid ${USERS_FILTER.ctrlBorder}` : merged.border,
                borderColor: border ? USERS_FILTER.ctrlBorder : merged.borderColor,
                boxShadow: state.isFocused
                    ? '0 0 0 2px rgba(125, 184, 255, 0.45)'
                    : merged.boxShadow,
                '&:hover': {
                    ...merged['&:hover'],
                    borderColor: '#b8cad8',
                },
            };
        },
        menu: (b) => ({
            ...base.menu(b),
            background: USERS_FILTER.menuBg,
            border: `1px solid ${USERS_FILTER.menuBorder}`,
            borderColor: USERS_FILTER.menuBorder,
            borderRadius: 6,
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.45)',
            zIndex: CMS_SELECT_PORTAL_Z,
        }),
        menuList: (b) => ({
            ...base.menuList(b),
            background: USERS_FILTER.menuBg,
            padding: 6,
        }),
        option: (b, state) => ({
            ...base.option(b, state),
            color: USERS_FILTER.text,
            backgroundColor:
                state.isSelected || state.isFocused || state.isActive
                    ? USERS_FILTER.menuHover
                    : 'transparent',
        }),
        singleValue: (b) => ({ ...base.singleValue(b), color: USERS_FILTER.text }),
        placeholder: (b) => ({ ...base.placeholder(b), color: USERS_FILTER.textMuted }),
        input: (b) => ({ ...base.input(b), color: USERS_FILTER.text }),
        indicatorSeparator: (b) => ({
            ...base.indicatorSeparator(b),
            backgroundColor: USERS_FILTER.ctrlBorder,
        }),
        dropdownIndicator: (b) => ({
            ...base.dropdownIndicator(b),
            color: USERS_FILTER.textMuted,
            '&:hover': { color: USERS_FILTER.text },
        }),
        clearIndicator: (b) => ({
            ...base.clearIndicator(b),
            color: USERS_FILTER.textMuted,
            '&:hover': { color: USERS_FILTER.text },
        }),
        multiValue: (b) => ({
            ...base.multiValue(b),
            backgroundColor: USERS_FILTER.menuHover,
        }),
        multiValueLabel: (b) => ({
            ...base.multiValueLabel(b),
            color: USERS_FILTER.text,
        }),
        multiValueRemove: (b) => ({
            ...base.multiValueRemove(b),
            color: USERS_FILTER.textMuted,
            ':hover': {
                backgroundColor: USERS_FILTER.menuBorder,
                color: USERS_FILTER.text,
            },
        }),
    });
}
